import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Título de la pestaña por ruta: "<Página> · urbania360".
 *
 * El router de esta app es `<Routes>` sin objetos de ruta (no hay `handle`
 * ni `useMatches`), así que el mapa vive acá: una tabla ordenada de
 * patrones. `index.html` trae el título genérico y el worker reescribe el
 * de `/t/<id>` para los crawlers; esto cubre la navegación de un usuario.
 *
 * `null` = esta ruta pone su propio título (el visor, con el nombre del
 * tour) o no tiene (embed). Una ruta que no matchea es un 404.
 */
const BRAND = 'urbania360';

const TITLES: [RegExp, string | null][] = [
  [/^\/t\/[^/]+\/?$/, null],
  [/^\/o\/[^/]+\/?$/, null],
  [/^\/login\/?$/, 'Iniciar sesión'],
  [/^\/signup\/?$/, 'Crear cuenta'],
  [/^\/auth\/callback\/?$/, 'Iniciando sesión'],
  [/^\/accept-invite\/[^/]+\/?$/, 'Invitación'],
  [/^\/forgot-password\/?$/, 'Recuperar contraseña'],
  [/^\/reset-password\/?$/, 'Nueva contraseña'],
  [/^\/connect\/?$/, 'Conectar urbania3d'],
  [/^\/admin\/?$/, 'Panel'],
  [/^\/admin\/account\/?$/, 'Mi cuenta'],
  [/^\/admin\/help(\/[^/]+)?\/?$/, 'Centro de ayuda'],
  [/^\/admin\/organizations\/new\/?$/, 'Nueva organización'],
  [/^\/admin\/organizations\/[^/]+\/?$/, 'Organización'],
  [/^\/admin\/organizations\/[^/]+\/members\/?$/, 'Miembros'],
  [/^\/admin\/organizations\/[^/]+\/settings\/?$/, 'Ajustes de la organización'],
  [/^\/admin\/organizations\/[^/]+\/billing\/?$/, 'Plan y uso'],
  [/^\/admin\/organizations\/[^/]+\/stats\/?$/, 'Estadísticas'],
  // `/projects` sola redirige a la org: no lleva título propio.
  [/^\/admin\/organizations\/[^/]+\/projects\/?$/, null],
  [/^\/admin\/organizations\/[^/]+\/projects\/[^/]+\/?$/, 'Colección'],
  [/^\/admin\/organizations\/[^/]+\/projects\/[^/]+\/edit\/?$/, 'Editar colección'],
  [/^\/admin\/organizations\/[^/]+\/projects\/[^/]+\/tours\/[^/]+\/?$/, 'Editar tour'],
  [
    /^\/admin\/organizations\/[^/]+\/projects\/[^/]+\/tours\/[^/]+\/panoramas\/[^/]+\/?$/,
    'Editor de panoramas',
  ],
];

/** `undefined` si la ruta no existe (404), `null` si no hay título que poner. */
export function titleForPath(pathname: string): string | null | undefined {
  for (const [pattern, title] of TITLES) {
    if (pattern.test(pathname)) return title;
  }
  return pathname === '/' ? null : undefined;
}

export function formatTitle(page: string): string {
  return `${page} · ${BRAND}`;
}

/** Lo que una página declara de su título: partes de la más específica a la menos, y si tiene cambios sin guardar. */
export interface DeclaredTitle {
  parts: string[];
  dirty: boolean;
}

/**
 * Estado compartido entre las páginas y `useRouteTitle`. Solo `useRouteTitle`
 * escribe `document.title`: así no hay carrera entre el efecto del router
 * (que corre después que el de la página hija) y el de la página.
 */
const declaredTitleAtom = atom<DeclaredTitle | null>(null);

/**
 * Título final. Con partes declaradas: "<parte> · <parte> · urbania360"; sin
 * ninguna (la página todavía carga) cae al título genérico de la ruta. `null`
 * = no tocar el título. Con cambios sin guardar se antepone "● ".
 */
export function composeTitle(generic: string | null, declared: DeclaredTitle | null): string | null {
  if (generic === null) return null;
  const parts = declared?.parts.filter(part => part.trim() !== '') ?? [];
  const title = formatTitle(parts.length > 0 ? parts.join(' · ') : generic);
  return declared?.dirty ? `● ${title}` : title;
}

/**
 * Una página con una entidad (tour, panorama, org…) declara acá su título,
 * de lo más específico a lo menos. Partes vacías o `undefined` se descartan.
 */
export function usePageTitle(parts: (string | null | undefined)[], options?: { dirty?: boolean }): void {
  const setDeclared = useSetAtom(declaredTitleAtom);
  const dirty = options?.dirty ?? false;
  const key = parts.map(part => part ?? '').join('\u0000');
  useEffect(() => {
    setDeclared({ parts: key.split('\u0000'), dirty });
  }, [key, dirty, setDeclared]);
  useEffect(() => () => setDeclared(null), [setDeclared]);
}

/** Partes del título que declaró la página actual (vacío mientras carga o si no declaró ninguna). */
export function useDeclaredTitleParts(): string[] {
  return useAtomValue(declaredTitleAtom)?.parts.filter(part => part.trim() !== '') ?? [];
}

/** Monta una vez dentro del router; sigue la ubicación. */
export function useRouteTitle(enabled = true): void {
  const { pathname } = useLocation();
  const declared = useAtomValue(declaredTitleAtom);
  useEffect(() => {
    if (!enabled) return;
    const page = titleForPath(pathname);
    const title = composeTitle(page === undefined ? 'Página no encontrada' : page, declared);
    if (title !== null) document.title = title;
  }, [pathname, enabled, declared]);
}
